import React, { useRef, useState, useCallback, useEffect } from 'react';
import HTMLFlipBook from 'react-pageflip';
import CoverPage from './CoverPage';
import TitlePage from './TitlePage';
import TocPage from './TocPage';
import CharacterPage from './CharacterPage';
import ChapterPage from './ChapterPage';
import EndPage from './EndPage';
import { registerParticipants } from '../../../utils/nameColors';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const ASPECT_RATIO = 0.715; // width/height, A5-ish

export default function SampleBookViewer({ onClose, bookMeta, bookCharacters, bookChapters, bookStories, coverImage }) {
  const bookRef = useRef(null);
  const contentRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [dimensions, setDimensions] = useState({ width: 380, height: 532 });

  useEffect(() => {
    function calcSize() {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isMobile = vw < 768;
      const navSpace = isMobile ? 2 * (36 + 8) : 2 * (40 + 16);
      const verticalReserve = isMobile ? 160 : 128;
      const maxW = vw - 48 - navSpace;
      const maxH = vh - verticalReserve;

      let h = Math.min(maxH, 532);
      let w = h * ASPECT_RATIO;
      if (w > maxW) {
        w = maxW;
        h = w / ASPECT_RATIO;
      }
      setDimensions({ width: Math.round(w), height: Math.round(h) });
    }

    calcSize();
    window.addEventListener('resize', calcSize);
    return () => window.removeEventListener('resize', calcSize);
  }, []);

  useEffect(() => {
    registerParticipants(bookCharacters.map((c) => c.name));
  }, [bookCharacters]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const totalPages = 4 + bookStories.length + 1;

  const handleFlip = useCallback((e) => {
    setCurrentPage(e.data);
  }, []);

  const flipPrev = useCallback(() => {
    bookRef.current?.pageFlip()?.flipPrev('top');
  }, []);

  const flipNext = useCallback(() => {
    bookRef.current?.pageFlip()?.flipNext('top');
  }, []);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'ArrowLeft') flipPrev();
      else if (e.key === 'ArrowRight') flipNext();
      else if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [flipPrev, flipNext, onClose]);

  const handleBackdropClick = (e) => {
    if (contentRef.current && !contentRef.current.contains(e.target)) {
      onClose?.();
    }
  };

  return (
    <div className="sbv-overlay" onClick={handleBackdropClick}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,700;1,9..40,400&family=Space+Mono:wght@400;700&display=swap');

        .sbv-overlay {
          position: fixed;
          inset: 0;
          z-index: 50;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          animation: sbvFadeIn 0.25s ease-out;
          padding: 24px;
        }

        @keyframes sbvFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes sbvScaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }

        .sbv-content {
          animation: sbvScaleIn 0.3s ease-out;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
        }

        /* Close button - top right of viewport */
        .sbv-close {
          position: fixed;
          top: 16px;
          right: 16px;
          z-index: 51;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: rgba(255, 255, 255, 0.8);
          cursor: pointer;
          transition: all 0.2s ease;
          backdrop-filter: blur(8px);
        }

        .sbv-close:hover {
          background: rgba(255, 255, 255, 0.2);
          color: #FFFFFF;
        }

        /* Book row: prev button | book | next button */
        .sbv-book-row {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .sbv-nav-btn {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.15);
          color: rgba(255, 255, 255, 0.7);
          cursor: pointer;
          transition: all 0.2s ease;
          backdrop-filter: blur(4px);
          flex-shrink: 0;
        }

        .sbv-nav-btn:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.2);
          color: #FFFFFF;
          border-color: rgba(255, 255, 255, 0.3);
        }

        .sbv-nav-btn:disabled {
          opacity: 0.2;
          cursor: default;
        }

        /* Constrain react-pageflip */
        .stf__parent {
          overflow: hidden !important;
          border-radius: 4px;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
          background: #FFFFFF;
          pointer-events: none;
        }

        .stf__block {
          overflow: hidden !important;
          background: #FFFFFF;
        }

        .sbv-endpage-cta {
          pointer-events: auto !important;
        }

        .book-page {
          background: #FFFFFF !important;
          overflow: hidden;
          position: relative;
          width: 100%;
          height: 100%;
        }

        .book-page-inner {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .book-page-number {
          position: absolute;
          bottom: 6px;
          right: 8px;
          font-family: 'Space Mono', monospace;
          font-size: 7px;
          font-weight: 700;
          color: #9CA3AF;
        }

        /* Bottom bar: dots + page count */
        .sbv-footer {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .sbv-dots {
          display: flex;
          gap: 5px;
          align-items: center;
        }

        .sbv-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.25);
          transition: all 0.3s ease;
          cursor: pointer;
          border: none;
          padding: 0;
        }

        .sbv-dot.active {
          background: #F9A8D4;
          width: 18px;
          border-radius: 3px;
        }

        .sbv-page-count {
          font-family: 'Space Mono', monospace;
          font-size: 11px;
          color: rgba(255, 255, 255, 0.4);
        }
      `}</style>

      {/* Fixed close button */}
      <button className="sbv-close" onClick={onClose} title="Close preview">
        <XMarkIcon className="w-5 h-5" />
      </button>

      <div ref={contentRef} className="sbv-content">
        {/* Book row: prev | book | next */}
        <div className="sbv-book-row">
          <button className="sbv-nav-btn" onClick={flipPrev} disabled={currentPage <= 0}>
            <ChevronLeftIcon className="w-5 h-5" />
          </button>

          <HTMLFlipBook
            ref={bookRef}
            width={dimensions.width}
            height={dimensions.height}
            size="fixed"
            showCover={true}
            flippingTime={700}
            usePortrait={true}
            startZIndex={0}
            autoSize={true}
            maxShadowOpacity={0.4}
            drawShadow={true}
            mobileScrollSupport={false}
            onFlip={handleFlip}
            startPage={0}
            clickEventForward={false}
            useMouseEvents={true}
            swipeDistance={9999}
            showPageCorners={false}
            disableFlipByClick={false}
          >
            <CoverPage
              title={bookMeta.title}
              subtitle={bookMeta.subtitle}
              year={bookMeta.year}
              coverImage={coverImage}
            />
            <TitlePage
              title={bookMeta.title}
              subtitle={bookMeta.subtitle}
              year={bookMeta.year}
              dedication={bookMeta.dedication}
            />
            <TocPage chapters={bookChapters} pageNumber={2} />
            <CharacterPage characters={bookCharacters} pageNumber={3} />
            {bookStories.map((story, idx) => (
              <ChapterPage
                key={story.story_id}
                story={story}
                pageNumber={4 + idx}
              />
            ))}
            <EndPage onCreateBook={() => {}} />
          </HTMLFlipBook>

          <button className="sbv-nav-btn" onClick={flipNext} disabled={currentPage >= totalPages - 1}>
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Dots + page count */}
        <div className="sbv-footer">
          <div className="sbv-dots">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                className={`sbv-dot ${i === currentPage ? 'active' : ''}`}
                onClick={() => bookRef.current?.pageFlip()?.turnToPage(i)}
              />
            ))}
          </div>
          <span className="sbv-page-count">{currentPage + 1} / {totalPages}</span>
        </div>

      </div>
    </div>
  );
}
